import React from 'react';
import { MagnifyingGlassIcon, CalendarDaysIcon, XMarkIcon } from '@heroicons/react/24/outline';

/**
 * Primitivos de filtro reutilizáveis (padrão DESIGN_SYSTEM: barra de filtros logo
 * abaixo do header, acima dos KPIs). Garante o mesmo visual/altura/dark em todas as
 * telas de lista. Compõe-se livremente: <BarraFiltros> envolve <FiltroBusca>,
 * <FiltroPeriodo> e quaisquer chips/selects específicos da tela.
 */

/** Container da barra de filtros — card bordeado, empilha no mobile. */
export const BarraFiltros: React.FC<{ children: React.ReactNode; className?: string }> = ({
  children,
  className = '',
}) => (
  <div
    className={`flex flex-col gap-2 rounded-iris-lg border border-line bg-surface p-2 sm:flex-row sm:flex-wrap sm:items-center ${className}`}
  >
    {children}
  </div>
);

/** Campo de busca com ícone. Full-width no mobile, 18rem no desktop. */
export const FiltroBusca: React.FC<{
  value: string;
  onChange: (valor: string) => void;
  placeholder?: string;
}> = ({ value, onChange, placeholder = 'Buscar...' }) => (
  <div className="relative w-full sm:w-72">
    <MagnifyingGlassIcon className="pointer-events-none absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-tx-3" />
    <input
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="h-11 w-full rounded-iris-sm border border-line-strong bg-surface-2 pl-8 pr-2 text-sm text-tx placeholder:text-tx-3 outline-none focus:border-accent focus:bg-surface focus:ring-[3px] focus:ring-accent-soft md:h-9"
    />
  </div>
);

/**
 * Controle único de intervalo de datas (De–Até). Um ícone só (indicadores nativos
 * escondidos), abre o picker no clique, "✕" limpa. Dispara `onChange(inicio, fim)`
 * a cada alteração — o pai decide o efeito (setState, resetar paginação, etc).
 */
export const FiltroPeriodo: React.FC<{
  inicio: string;
  fim: string;
  onChange: (inicio: string, fim: string) => void;
}> = ({ inicio, fim, onChange }) => (
  <div className="flex h-11 w-full items-center gap-1.5 rounded-iris-sm border border-line-strong bg-surface-2 px-2 text-sm md:h-9 sm:w-auto">
    <CalendarDaysIcon className="h-4 w-4 flex-shrink-0 text-tx-3" />
    <input
      type="date"
      value={inicio}
      onChange={(e) => onChange(e.target.value, fim)}
      onClick={(e) => e.currentTarget.showPicker?.()}
      aria-label="Data inicial"
      className="border-0 bg-transparent p-0 text-tx outline-none [color-scheme:light] dark:[color-scheme:dark] [&::-webkit-calendar-picker-indicator]:hidden"
    />
    <span className="text-tx-3">–</span>
    <input
      type="date"
      value={fim}
      onChange={(e) => onChange(inicio, e.target.value)}
      onClick={(e) => e.currentTarget.showPicker?.()}
      aria-label="Data final"
      className="border-0 bg-transparent p-0 text-tx outline-none [color-scheme:light] dark:[color-scheme:dark] [&::-webkit-calendar-picker-indicator]:hidden"
    />
    {(inicio || fim) && (
      <button
        type="button"
        onClick={() => onChange('', '')}
        title="Limpar período"
        className="ml-auto flex-shrink-0 rounded-iris-sm p-0.5 text-tx-3 hover:bg-surface-2 hover:text-tx-2 sm:ml-1"
      >
        <XMarkIcon className="h-3.5 w-3.5" />
      </button>
    )}
  </div>
);
